import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// Components
import SimpleLoading from '../../../../components/common/SimpleLoading';
import AttachmentsFile from './commentsSection/AttachmentsFile';
import PickRecAudio from './commentsSection/PickRecAudio';
import PickRecVideo from './commentsSection/PickRecVideo';

// Material UI
import Button from '@mui/material/Button';
import CommonButton from '../../../../components/common/CommonButton';

const CommentsSection = (props) => {
    const {
        comment,
        activeRol,
        handleComment,
        loading,
        handlePrimarySend,
        isPrimaryDisabled,
        submitLabel,
        placeholder,
        onCancel,
        showTextarea = true,
        cancelLabel = 'Cancelar edición',
        showCancel = true,
        showAttachmentPickers = false,
        handleSendAudio,
        handleSendVideo,
        handleSendFile,
        teacherFiles,
        idTask,
    } = props;

    const isCommentDisabled =
        activeRol !== 'docente' && activeRol !== 'academico';
    const commentTextAreaRef = useRef(null);

    const autoResizeComment = () => {
        const textArea = commentTextAreaRef.current;
        if (!textArea) return;
        textArea.style.height = 'auto';
        textArea.style.height = `${textArea.scrollHeight}px`;
    };

    useEffect(() => {
        autoResizeComment();
    }, [comment]);

    const showComposerChrome = showTextarea || showAttachmentPickers;

    return (
        <FieldOuter $compactTop={!showComposerChrome}>
            {showComposerChrome && (
                <FloatingLabel>Comentario de corrección</FloatingLabel>
            )}
            {showComposerChrome && (
                <ComposerWrapper>
                    {showTextarea && (
                        <CommentTextArea
                            ref={commentTextAreaRef}
                            title={comment}
                            placeholder={placeholder}
                            value={comment}
                            disabled={isCommentDisabled || loading}
                            onChange={handleComment}
                            onInput={autoResizeComment}
                        />
                    )}
                    {showAttachmentPickers && (
                        <>
                            {showTextarea && <ToolbarSeparator />}
                            <ToolbarRowPickers>
                                <ContentAttachments>
                                    <PickRecAudio
                                        handleSendAudio={handleSendAudio}
                                        audioFile={
                                            teacherFiles?.audio_correccion?.url
                                        }
                                        idTask={idTask}
                                    />
                                    <PickRecVideo
                                        handleSendVideo={handleSendVideo}
                                        videoFile={
                                            teacherFiles?.video_correccion?.url
                                        }
                                        idTask={idTask}
                                    />
                                    <AttachmentsFile
                                        handleSendFile={handleSendFile}
                                        file={
                                            teacherFiles?.archivo_correccion
                                                ?.url
                                        }
                                        idTask={idTask}
                                    />
                                </ContentAttachments>
                            </ToolbarRowPickers>
                        </>
                    )}
                </ComposerWrapper>
            )}
            <EditorActionsBar $tightTop={!showComposerChrome}>
                <ConversationActions>
                    {showCancel && !loading && (
                        <CancelButton
                            variant="text"
                            label={cancelLabel}
                            type="button"
                            onClick={onCancel}
                        />
                    )}
                    {!loading ? (
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handlePrimarySend}
                            disabled={isPrimaryDisabled}
                        >
                            {submitLabel}
                        </Button>
                    ) : (
                        <SimpleLoading />
                    )}
                </ConversationActions>
            </EditorActionsBar>
        </FieldOuter>
    );
};

export default CommentsSection;

const FieldOuter = styled.div`
    position: relative;
    width: 100%;
    padding-top: ${(p) => (p.$compactTop ? '0' : '10px')};
    margin-bottom: 1rem;
`;

const ComposerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #616161;
    border-radius: 18px;
    overflow: clip;
    width: 100%;
    box-sizing: border-box;
`;

const FloatingLabel = styled.span`
    position: absolute;
    top: 10px;
    left: 14px;
    transform: translateY(-50%);
    z-index: 1;
    padding: 0 6px;
    background: white;
    color: #616161;
    font-size: 0.85rem;
    font-weight: 500;
    line-height: 1.2;
    pointer-events: none;
`;

const CommentTextArea = styled.textarea`
    width: 100%;
    min-height: 160px;
    resize: vertical;
    overflow: hidden;
    box-sizing: border-box;
    padding: 20px 14px 12px;
    font-size: 16px;
    background: transparent;
    border: none;
    min-width: 0;
    outline: none;
    font-family: inherit;

    &::placeholder {
        color: #999;
    }

    &::-webkit-resizer {
        border-width: 10px;
        border-style: solid;
        border-color: transparent #616161 #616161 transparent;
    }
`;

const ToolbarSeparator = styled.div`
    width: 100%;
    height: 1px;
    background: #616161;
    flex-shrink: 0;
`;

const ToolbarRowPickers = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    min-height: 48px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        flex-wrap: wrap;
    }
`;

const EditorActionsBar = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    padding-top: ${(p) => (p.$tightTop ? '0' : '10px')};
    box-sizing: border-box;
`;

const ContentAttachments = styled.div`
    display: flex;
    gap: 0.25rem;
    align-items: center;
    flex-wrap: wrap;
    min-width: 0;
`;

const ConversationActions = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    min-width: 0;

    @media (max-width: 768px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const CancelButton = styled(CommonButton)`
    background-color: transparent;
    color: #b31d15;
    border: 1px solid #b31d15;
    &:hover {
        background-color: #b31d15;
        color: #fff;
    }
`;
